import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminPanel from '../AdminPanel';
import { useAuth, getAuthErrorMessage } from '../hooks/useAuth';
import { API_BASE_URL } from '../config';

export default function AdminGate() {
  const navigate = useNavigate();
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const auth = useAuth({
    onVerifySuccess: () => {},
    onLoginSuccess: () => {
      setLoginForm({ username: '', password: '' });
      setLoginError('');
    },
    onLogout: () => {},
  });

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    const { username, password } = loginForm;
    setLoginError('');
    if (!username?.trim() || !password) {
      setLoginError('Username and password are required.');
      return;
    }
    try {
      setIsLoggingIn(true);
      await auth.login(username.trim(), password);
    } catch (err) {
      setLoginError(getAuthErrorMessage(err, 'Invalid credentials'));
    } finally {
      setIsLoggingIn(false);
    }
  }, [loginForm, auth]);

  const handleBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  if (auth.isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (auth.isAuthenticated) {
    return (
      <AdminPanel
        authToken={auth.token}
        onAuthExpired={() => auth.logout('Session expired. Please log in again.')}
        onBack={handleBack}
        backendUrl={API_BASE_URL}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-sm bg-white rounded-lg shadow p-6">
        <h1 className="text-xl font-semibold mb-4">Management • Admin Login</h1>
        <form onSubmit={handleLogin} className="space-y-3">
          <input
            type="text"
            placeholder="Username"
            className="border w-full p-2 rounded"
            value={loginForm.username}
            onChange={(e) => setLoginForm((f) => ({ ...f, username: e.target.value }))}
            autoComplete="username"
          />
          <input
            type="password"
            placeholder="Password"
            className="border w-full p-2 rounded"
            value={loginForm.password}
            onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
            autoComplete="current-password"
          />
          {loginError && <p className="text-sm text-red-600">{loginError}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={isLoggingIn}
              className="flex-1 bg-indigo-600 text-white py-2 rounded disabled:opacity-60"
            >
              {isLoggingIn ? 'Logging in...' : 'Login'}
            </button>
            <Link to="/" className="flex-1 text-center py-2 border rounded text-gray-700 hover:bg-gray-50">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
